import React, { createContext, useContext, useEffect } from 'react';
import { analyzeBundleSize, collectPerformanceMetrics, monitorMemoryUsage } from '../../utils/bundleAnalyzer';

const PerformanceContext = createContext();

export const usePerformance = () => {
  const context = useContext(PerformanceContext);
  if (!context) {
    throw new Error('usePerformance must be used within a PerformanceProvider');
  }
  return context;
};

const PerformanceProvider = ({ children }) => {
  useEffect(() => {
    // Initialize performance monitoring
    const initPerformanceMonitoring = () => {
      analyzeBundleSize();
      collectPerformanceMetrics();
      
      // Monitor memory usage every 30 seconds in development
      if (process.env.NODE_ENV === 'development') {
        const memoryInterval = setInterval(() => {
          monitorMemoryUsage();
        }, 30000);

        return () => clearInterval(memoryInterval);
      }
    };

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initPerformanceMonitoring);
    } else {
      initPerformanceMonitoring();
    }

    // Monitor page visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        collectPerformanceMetrics();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('DOMContentLoaded', initPerformanceMonitoring);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const value = {
    analyzeBundleSize,
    collectPerformanceMetrics,
    monitorMemoryUsage
  };

  return (
    <PerformanceContext.Provider value={value}>
      {children}
    </PerformanceContext.Provider>
  );
};

export default PerformanceProvider;
